/* ----------------------------------------------------------
   RCCS FORUM — CLIENT SIDE JS
   - Anyone can view posts, media, comments, reactions
   - Login required for posting, commenting, reacting
   - Demo mode (no real backend yet)
   - Includes basic front-end moderation:
     * Admin/mod roles from customer tags
     * Master toggle: approval for posts with media/links
     * Pending posts only visible to admins/mods
   ---------------------------------------------------------- */

(function () {
  const root = document.getElementById("rccs-forum-app");
  if (!root) return;

  // Whether Shopify customer is logged in
  const isLoggedIn = !!window.RCCS_CUSTOMER;

  // Login URL (pulled from data-login-url if you set it in forum.liquid)
  const loginUrl =
    root.getAttribute("data-login-url") ||
    "/account/login"; // fallback, fine in JS

  const USE_DEMO = true; // demo only, no backend yet
  const API_BASE = "https://YOUR-FORUM-API-URL.com"; // placeholder

  const REACTIONS = [
    { id: "like",  label: "Like",  emoji: "👍" },
    { id: "love",  label: "Love",  emoji: "❤️" },
    { id: "haha",  label: "Haha",  emoji: "😂" },
    { id: "wow",   label: "Wow",   emoji: "😮" },
    { id: "sad",   label: "Sad",   emoji: "😢" },
    { id: "angry", label: "Angry", emoji: "😡" }
  ];

  const URL_REGEX = /(https?:\/\/[^\s]+)/gi;
  const HAS_URL_REGEX = /(https?:\/\/[^\s]+)/i;

  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  /* --------------------------------------------------------
     ROLE HELPERS (admin / mod)
     -------------------------------------------------------- */

  function getCustomerTags() {
    if (!window.RCCS_CUSTOMER || !window.RCCS_CUSTOMER.tags) return [];
    if (Array.isArray(window.RCCS_CUSTOMER.tags)) {
      return window.RCCS_CUSTOMER.tags.map((t) => String(t).toLowerCase());
    }
    return String(window.RCCS_CUSTOMER.tags)
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
  }

  function isAdmin() {
    if (!window.RCCS_CUSTOMER) return false;
    const tags = getCustomerTags();
    return tags.includes("forum-admin");
  }

  function isModerator() {
    if (!window.RCCS_CUSTOMER) return false;
    const tags = getCustomerTags();
    return isAdmin() || tags.includes("forum-mod");
  }

  const MOD_SETTINGS_KEY = "rccs_forum_mod_settings_v1";

  const defaultModerationSettings = {
    approvalEnabled: true,          // master ON/OFF
    mediaLinksOnly: true            // only posts w/ image/video/link need approval
  };

  function loadModerationSettings() {
    try {
      const raw = localStorage.getItem(MOD_SETTINGS_KEY);
      if (!raw) return { ...defaultModerationSettings };
      const parsed = JSON.parse(raw);
      return {
        approvalEnabled:
          typeof parsed.approvalEnabled === "boolean"
            ? parsed.approvalEnabled
            : defaultModerationSettings.approvalEnabled,
        mediaLinksOnly:
          typeof parsed.mediaLinksOnly === "boolean"
            ? parsed.mediaLinksOnly
            : defaultModerationSettings.mediaLinksOnly
      };
    } catch (e) {
      return { ...defaultModerationSettings };
    }
  }

  function saveModerationSettings(settings) {
    try {
      localStorage.setItem(MOD_SETTINGS_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn("Could not save moderation settings", e);
    }
  }

  /* --------------------------------------------------------
     DEMO DATA — 15 sample posts, random names
     -------------------------------------------------------- */

  function hoursAgo(h) {
    return new Date(Date.now() - h * 60 * 60 * 1000).toISOString();
  }

  const demoPostsBase = [
    {
      id: "demo1",
      title: "Night Crawl at Ridge Line",
      authorName: "Alex",
      authorEmail: "alex@example.com",
      text: "First shake-down run with the RCCS inverted skid + panels. Breakover is insane now. Cleared gate 10 like it was nothing.",
      imageUrl: "https://via.placeholder.com/900x500/111827/ffffff?text=Night+Crawl+Demo",
      videoUrl: "",
      createdAt: hoursAgo(6),
      status: "approved",
      comments: [
        {
          authorName: "Jordan",
          text: "Those panels look wild in the dark. What wheelbase are you running?",
          createdAt: hoursAgo(5)
        },
        {
          authorName: "Sky",
          text: "Love seeing the inverted skid out on the rocks 🔥",
          createdAt: hoursAgo(4.5)
        }
      ],
      reactions: [
        { userId: "user1", userName: "Jordan", reaction: "love" },
        { userId: "user2", userName: "Sky", reaction: "like" },
        { userId: "user3", userName: "Riley", reaction: "wow" },
        { userId: "user4", userName: "Blake", reaction: "love" },
        { userId: "user5", userName: "Casey", reaction: "haha" },
        { userId: "user6", userName: "Logan", reaction: "like" },
        { userId: "user7", userName: "Quinn", reaction: "like" }
      ]
    },
    {
      id: "demo2",
      title: "Backyard Comp Training",
      authorName: "Morgan",
      authorEmail: "morgan@example.com",
      text: "Built a mini backyard comp line using RCCS gates. Trying to practice side-hill lines before the next local comp.",
      imageUrl: "https://via.placeholder.com/900x500/0b1120/ffffff?text=Backyard+Comp+Line",
      videoUrl: "",
      createdAt: hoursAgo(24),
      status: "approved",
      comments: [
        {
          authorName: "Rowan",
          text: "That 3rd gate looks like a tire eater 😂",
          createdAt: hoursAgo(20)
        },
        {
          authorName: "Harper",
          text: "That drop off on the right is spicy.",
          createdAt: hoursAgo(19)
        },
        {
          authorName: "Avery",
          text: "I need those gates for my yard now.",
          createdAt: hoursAgo(18)
        },
        {
          authorName: "Sky",
          text: "Saving this layout as inspo.",
          createdAt: hoursAgo(17)
        }
      ],
      reactions: [
        { userId: "user8", userName: "Rowan", reaction: "haha" },
        { userId: "user9", userName: "Harper", reaction: "like" },
        { userId: "user10", userName: "Parker", reaction: "like" },
        { userId: "user11", userName: "Reese", reaction: "love" }
      ]
    },
    {
      id: "demo3",
      title: "Teal Panel Refresh",
      authorName: "Taylor",
      authorEmail: "taylor@example.com",
      text: "Just swapped to the new RCCS panel set in teal/black. Matches my beadlocks perfectly. Check the full walk-around: https://youtu.be/dQw4wWgXcQ",
      imageUrl: "https://via.placeholder.com/900x500/020617/ffffff?text=Teal+Panel+Refresh",
      videoUrl: "",
      createdAt: hoursAgo(48),
      status: "approved",
      comments: [
        {
          authorName: "Casey",
          text: "That color combo is so clean.",
          createdAt: hoursAgo(40)
        },
        {
          authorName: "Riley",
          text: "Teal gang 🐍",
          createdAt: hoursAgo(36)
        },
        {
          authorName: "Jamie",
          text: "These pics sold me on that panel set.",
          createdAt: hoursAgo(35)
        }
      ],
      reactions: [
        { userId: "user12", userName: "Casey", reaction: "love" },
        { userId: "user13", userName: "Riley", reaction: "love" },
        { userId: "user14", userName: "Jamie", reaction: "like" },
        { userId: "user15", userName: "Logan", reaction: "wow" },
        { userId: "user16", userName: "Harper", reaction: "like" },
        { userId: "user17", userName: "Parker", reaction: "wow" }
      ]
    },
    {
      id: "demo4",
      title: "Scale Garage Detail Pass",
      authorName: "Rowan",
      authorEmail: "rowan@example.com",
      text: "Spent the afternoon detailing the scale garage. New posters, oil drums, and a tiny RCCS banner above the pit.",
      imageUrl: "https://via.placeholder.com/900x500/020617/ffffff?text=Scale+Garage+Detail",
      videoUrl: "",
      createdAt: hoursAgo(60),
      status: "approved",
      comments: [],
      reactions: [
        { userId: "user18", userName: "Alex", reaction: "like" },
        { userId: "user19", userName: "Blake", reaction: "wow" }
      ]
    },
    {
      id: "demo5",
      title: "Rock Garden Shakeout",
      authorName: "Logan",
      authorEmail: "logan@example.com",
      text: "Took the new rig through the local rock garden. Zero hang-ups thanks to the inverted skid and low CG.",
      imageUrl: "https://via.placeholder.com/900x500/111827/ffffff?text=Rock+Garden+Session",
      videoUrl: "",
      createdAt: hoursAgo(70),
      status: "approved",
      comments: [
        {
          authorName: "Sky",
          text: "That second ledge looks brutal.",
          createdAt: hoursAgo(69)
        }
      ],
      reactions: [
        { userId: "user20", userName: "Sky", reaction: "wow" },
        { userId: "user21", userName: "Jordan", reaction: "like" }
      ]
    },
    {
      id: "demo6",
      title: "Night Trail Run",
      authorName: "Reese",
      authorEmail: "reese@example.com",
      text: "Trail run with just rock lights and a headlamp. The glow off these panels is wild.",
      imageUrl: "https://via.placeholder.com/900x500/0b1120/ffffff?text=Night+Trail+Glow",
      videoUrl: "",
      createdAt: hoursAgo(80),
      status: "approved",
      comments: [],
      reactions: [
        { userId: "user22", userName: "Drew", reaction: "love" },
        { userId: "user23", userName: "Harper", reaction: "love" },
        { userId: "user24", userName: "Alex", reaction: "like" }
      ]
    },
    {
      id: "demo7",
      title: "Waterfall Crossing",
      authorName: "Parker",
      authorEmail: "parker@example.com",
      text: "Had to try the waterfall crossing today. Electronics stayed bone dry. RCCS skid slid like butter.",
      imageUrl: "https://via.placeholder.com/900x500/020617/ffffff?text=Waterfall+Crossing",
      videoUrl: "",
      createdAt: hoursAgo(90),
      status: "approved",
      comments: [],
      reactions: [
        { userId: "user25", userName: "Morgan", reaction: "wow" }
      ]
    },
    {
      id: "demo8",
      title: "New Tire Test",
      authorName: "Jordan",
      authorEmail: "jordan@example.com",
      text: "Swapped to new sticky tires. Hooked up way better on the slick rock section.",
      imageUrl: "https://via.placeholder.com/900x500/111827/ffffff?text=New+Tire+Test",
      videoUrl: "",
      createdAt: hoursAgo(100),
      status: "approved",
      comments: [],
      reactions: [
        { userId: "user26", userName: "Taylor", reaction: "like" },
        { userId: "user27", userName: "Quinn", reaction: "haha" }
      ]
    },
    {
      id: "demo9",
      title: "Trail Meetup Clip",
      authorName: "Harper",
      authorEmail: "harper@example.com",
      text: "Short clip from today's trail meetup. Link here if you want the full video: https://www.youtube.com/watch?v=3GwjfUFyY6M",
      imageUrl: "",
      videoUrl: "",
      createdAt: hoursAgo(110),
      status: "approved",
      comments: [],
      reactions: [
        { userId: "user28", userName: "Sky", reaction: "love" }
      ]
    },
    {
      id: "demo10",
      title: "Fresh Paint",
      authorName: "Blake",
      authorEmail: "blake@example.com",
      text: "New paint scheme to match the RCCS panels. Purple fade with hex stencils.",
      imageUrl: "https://via.placeholder.com/900x500/0b1120/ffffff?text=Fresh+Paint+Fade",
      videoUrl: "",
      createdAt: hoursAgo(120),
      status: "approved",
      comments: [],
      reactions: [
        { userId: "user29", userName: "Alex", reaction: "love" }
      ]
    },
    {
      id: "demo11",
      title: "Bridge Line Reset",
      authorName: "Quinn",
      authorEmail: "quinn@example.com",
      text: "Rebuilt the bridge line so it's a bit less sketchy for the new drivers.",
      imageUrl: "https://via.placeholder.com/900x500/020617/ffffff?text=Bridge+Line+Reset",
      videoUrl: "",
      createdAt: hoursAgo(130),
      status: "approved",
      comments: [],
      reactions: []
    },
    {
      id: "demo12",
      title: "Sand Pit Fun",
      authorName: "Drew",
      authorEmail: "drew@example.com",
      text: "Tried a sand pit crawl. Not ideal but hilarious.",
      imageUrl: "https://via.placeholder.com/900x500/111827/ffffff?text=Sand+Pit+Fun",
      videoUrl: "",
      createdAt: hoursAgo(140),
      status: "approved",
      comments: [],
      reactions: []
    },
    {
      id: "demo13",
      title: "RCCS Product Shoutout",
      authorName: "Sky",
      authorEmail: "sky@example.com",
      text: "Dropped a quick review of my favorite part: https://rccaprasolutions.com/products/demo-panel-set",
      imageUrl: "",
      videoUrl: "",
      createdAt: hoursAgo(150),
      status: "approved",
      comments: [],
      reactions: [
        { userId: "user30", userName: "Jordan", reaction: "like" }
      ]
    },
    {
      id: "demo14",
      title: "Line of the Day",
      authorName: "Casey",
      authorEmail: "casey@example.com",
      text: "This was my favorite line of the day. Off-camber, tight gate, and a nasty undercut.",
      imageUrl: "https://via.placeholder.com/900x500/0b1120/ffffff?text=Line+of+the+Day",
      videoUrl: "",
      createdAt: hoursAgo(160),
      status: "approved",
      comments: [],
      reactions: []
    },
    {
      id: "demo15",
      title: "Club Night Comp",
      authorName: "Jamie",
      authorEmail: "jamie@example.com",
      text: "Club night comp highlights. Loving the gate markers from RCCS.",
      imageUrl: "https://via.placeholder.com/900x500/020617/ffffff?text=Club+Night+Comp",
      videoUrl: "",
      createdAt: hoursAgo(170),
      status: "approved",
      comments: [],
      reactions: [
        { userId: "user31", userName: "Logan", reaction: "like" },
        { userId: "user32", userName: "Harper", reaction: "love" }
      ]
    }
  ];

  /* --------------------------------------------------------
     BASIC HELPERS
     -------------------------------------------------------- */

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function getCurrentUserId() {
    if (!window.RCCS_CUSTOMER) return null;
    return String(
      window.RCCS_CUSTOMER.id || window.RCCS_CUSTOMER.email || "you"
    );
  }

  function getCurrentUserName() {
    if (!window.RCCS_CUSTOMER) return "You";
    return (
      window.RCCS_CUSTOMER.name ||
      window.RCCS_CUSTOMER.first_name ||
      window.RCCS_CUSTOMER.email ||
      "You"
    );
  }

  function formatRelativeTime(dateStr) {
    const d = new Date(dateStr);
    const now = new Date();
    let diff = Math.floor((now - d) / 1000);

    if (diff < 5) return "Just now";
    if (diff < 60) return diff + "s ago";

    const mins = Math.floor(diff / 60);
    if (mins < 60) return mins + "m ago";

    const hours = Math.floor(mins / 60);
    if (hours < 24) return hours + "h ago";

    const days = Math.floor(hours / 24);
    if (days < 30) return days + "d ago";

    const months = Math.floor(days / 30);
    if (months < 12) return months + "mo ago";

    const years = Math.floor(months / 12);
    return years + "y ago";
  }

  // Keep reaction bar from going off left/right edges
  function adjustReactionBarPosition(bar) {
    if (!bar) return;

    bar.style.transform = "translateX(-50%) translateY(0)";

    const rect = bar.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const padding = 8;

    let shift = 0;
    if (rect.left < padding) {
      shift = padding - rect.left;
    } else if (rect.right > vw - padding) {
      shift = (vw - padding) - rect.right;
    }

    if (shift !== 0) {
      bar.style.transform = `translateX(calc(-50% + ${shift}px)) translateY(0)`;
    }
  }

  /* --------------------------------------------------------
     STATE + WRAPPER (for text size & moderation & admin view)
     -------------------------------------------------------- */

  const state = {
    posts: [],
    loading: false,
    sortMode: "newest",
    searchQuery: "",
    textSize: "medium",
    pageSize: 20,
    currentPage: 1,
    moderation: loadModerationSettings(),
    // ADMIN VIEW FILTER:
    // "all"     = all posts (default)
    // "pending" = only pending posts
    // "mine"    = posts authored by current admin
    adminView: "all"
  };

  const wrapperEl =
    root.closest(".rccs-forum-wrapper") || root;

  function applyTextSize() {
    if (!wrapperEl) return;
    wrapperEl.classList.remove(
      "rccs-font-small",
      "rccs-font-medium",
      "rccs-font-large",
      "rccs-font-xlarge"
    );
    wrapperEl.classList.add("rccs-font-" + state.textSize);
  }

  /* --------------------------------------------------------
     INITIAL DOM (editor + toolbar + list + mod settings slot)
     -------------------------------------------------------- */

  root.innerHTML = `
    <div class="rccs-forum-new-post">
      <h3>Share Your RC Rig</h3>
      <input
        id="rccs-new-post-title"
        class="rccs-new-post-title"
        type="text"
        placeholder="Give your post a title (e.g. 'Capra Night Run at MMM')"
      >
      <textarea id="rccs-new-post-text" placeholder="Tell us about your build…"></textarea>
      <div class="rccs-new-post-media-row">
        <label class="rccs-upload-button" for="rccs-new-post-media">
          UPLOAD MEDIA
        </label>
        <input type="file" id="rccs-new-post-media" accept="image/*,video/*" />
        <span class="rccs-upload-filename" id="rccs-upload-filename"></span>
      </div>
      <button id="rccs-create-post">POST</button>
      <p id="rccs-new-post-status">${
        isLoggedIn
          ? ""
          : "Sign in to post, comment, or react."
      }</p>
    </div>

    <div class="rccs-forum-toolbar">
      <input
        id="rccs-search"
        class="rccs-toolbar-search"
        type="text"
        placeholder="Search posts by title, text, or author…"
      >
      <select id="rccs-sort" class="rccs-toolbar-select">
        <option value="newest">Newest first</option>
        <option value="oldest">Oldest first</option>
        <option value="activity">Recent activity</option>
      </select>
      <select id="rccs-text-size" class="rccs-toolbar-select">
        <option value="small">Text: Small</option>
        <option value="medium" selected>Text: Medium</option>
        <option value="large">Text: Large</option>
        <option value="xlarge">Text: Extra Large</option>
      </select>
      <select id="rccs-page-size" class="rccs-toolbar-select">
        <option value="20" selected>20 posts / page</option>
        <option value="40">40 posts / page</option>
        <option value="60">60 posts / page</option>
        <option value="80">80 posts / page</option>
        <option value="100">100 posts / page</option>
      </select>
    </div>

    <div id="rccs-mod-settings"></div>

    <div id="rccs-post-list">Loading posts…</div>
    <div id="rccs-pagination"></div>
  `;

  applyTextSize();

  /* --------------------------------------------------------
     MODERATION SETTINGS UI (admin/mod only) + ADMIN TOOLS
     -------------------------------------------------------- */

  function renderModerationSettingsUI() {
    const container = document.getElementById("rccs-mod-settings");
    if (!container) return;

    if (!isModerator()) {
      container.innerHTML = "";
      return;
    }

    const { approvalEnabled, mediaLinksOnly } = state.moderation;
    const adminUser = isAdmin();

    container.innerHTML = `
      <div class="rccs-mod-settings">
        <div class="rccs-mod-settings-header">
          <span class="rccs-mod-title">Moderator Controls</span>
          <span class="rccs-mod-subtitle">Only you and other mods/admins can see this.</span>
        </div>

        <div class="rccs-mod-row">
          <label class="rccs-mod-toggle">
            <input type="checkbox" id="rccs-mod-approval-enabled" ${approvalEnabled ? "checked" : ""}>
            <span class="rccs-mod-toggle-pill"></span>
            <span class="rccs-mod-label">Require approval for posts with media/links</span>
          </label>
        </div>

        <div class="rccs-mod-row rccs-mod-row-sub">
          <label class="rccs-mod-checkbox">
            <input type="checkbox" id="rccs-mod-media-links-only" ${mediaLinksOnly ? "checked" : ""}>
            <span class="rccs-mod-checkbox-box"></span>
            <span class="rccs-mod-label">Only review posts that have an image, video, or URL</span>
          </label>
        </div>

        <div class="rccs-mod-footer">
          <span class="rccs-mod-status">
            Moderation: <strong>${approvalEnabled ? "On" : "Off"}</strong>
          </span>
        </div>

        ${
          adminUser
            ? `
        <div class="rccs-admin-tools">
          <div class="rccs-admin-tools-header">
            <span class="rccs-admin-title">Admin Tools</span>
            <span class="rccs-admin-subtitle">Extra views just for forum admins.</span>
          </div>

          <div class="rccs-admin-tools-row">
            <button type="button" class="rccs-admin-view-btn" data-admin-view="all">
              All posts
            </button>
            <button type="button" class="rccs-admin-view-btn" data-admin-view="pending">
              Pending only
            </button>
            <button type="button" class="rccs-admin-view-btn" data-admin-view="mine">
              My posts
            </button>
          </div>

          <div class="rccs-admin-tools-row rccs-admin-tools-row-sub">
            <button type="button" class="rccs-admin-utility-btn" data-admin-scroll-pending>
              Jump to first pending post
            </button>
            <button type="button" class="rccs-admin-utility-btn" data-admin-reload>
              Reload posts
            </button>
          </div>
        </div>
        `
            : ""
        }
      </div>
    `;

    const chkApproval = document.getElementById("rccs-mod-approval-enabled");
    const chkMediaOnly = document.getElementById("rccs-mod-media-links-only");

    if (chkApproval) {
      chkApproval.addEventListener("change", () => {
        state.moderation.approvalEnabled = chkApproval.checked;
        saveModerationSettings(state.moderation);
        renderModerationSettingsUI();
        renderPosts();
      });
    }

    if (chkMediaOnly) {
      chkMediaOnly.addEventListener("change", () => {
        state.moderation.mediaLinksOnly = chkMediaOnly.checked;
        saveModerationSettings(state.moderation);
        renderModerationSettingsUI();
      });
    }

    // Admin-only tools wiring
    if (adminUser) {
      const viewButtons = container.querySelectorAll("[data-admin-view]");
      const reloadBtn = container.querySelector("[data-admin-reload]");
      const jumpBtn = container.querySelector("[data-admin-scroll-pending]");

      function updateViewButtonState() {
        viewButtons.forEach((btn) => {
          const view = btn.getAttribute("data-admin-view") || "all";
          if (view === state.adminView) {
            btn.classList.add("is-active");
          } else {
            btn.classList.remove("is-active");
          }
        });
      }

      if (viewButtons.length) {
        viewButtons.forEach((btn) => {
          btn.addEventListener("click", () => {
            const view = btn.getAttribute("data-admin-view") || "all";
            state.adminView = view;
            state.currentPage = 1;
            updateViewButtonState();
            renderPosts();
          });
        });
        updateViewButtonState();
      }

      if (reloadBtn) {
        reloadBtn.addEventListener("click", () => {
          fetchPosts();
        });
      }

      if (jumpBtn) {
        jumpBtn.addEventListener("click", () => {
          // Try to find any pending post on the current page first
          let target = document.querySelector(".rccs-forum-post--pending");
          if (!target) {
            alert("No pending posts are visible right now.");
            return;
          }
          target.scrollIntoView({ behavior: "smooth", block: "center" });
          target.classList.add("rccs-post-jump-highlight");
          setTimeout(() => {
            target.classList.remove("rccs-post-jump-highlight");
          }, 1500);
        });
      }
    }
  }

  /* --------------------------------------------------------
     TOOLBAR WIRING
     -------------------------------------------------------- */

  const textSizeSelect = document.getElementById("rccs-text-size");
  if (textSizeSelect) {
    textSizeSelect.value = state.textSize;
    textSizeSelect.addEventListener("change", (e) => {
      state.textSize = e.target.value || "medium";
      applyTextSize();
    });
  }

  const searchInput = document.getElementById("rccs-search");
  if (searchInput) {
    let searchTimer = null;
    searchInput.addEventListener("input", (e) => {
      if (searchTimer) clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        state.searchQuery = e.target.value || "";
        state.currentPage = 1;
        renderPosts();
      }, 150);
    });
  }

  const sortSelect = document.getElementById("rccs-sort");
  if (sortSelect) {
    sortSelect.value = state.sortMode;
    sortSelect.addEventListener("change", (e) => {
      state.sortMode = e.target.value || "newest";
      state.currentPage = 1;
      renderPosts();
    });
  }

  const pageSizeSelect = document.getElementById("rccs-page-size");
  if (pageSizeSelect) {
    pageSizeSelect.value = String(state.pageSize);
    pageSizeSelect.addEventListener("change", (e) => {
      const v = parseInt(e.target.value, 10) || 20;
      state.pageSize = v;
      state.currentPage = 1;
      renderPosts();
    });
  }

  function getLastActivityTime(post) {
    let t = new Date(post.createdAt).getTime();
    (post.comments || []).forEach((c) => {
      const ct = new Date(c.createdAt).getTime();
      if (ct > t) t = ct;
    });
    return t;
  }

  function getFilteredSortedPosts() {
    let posts = state.posts.slice();

    const q = state.searchQuery.trim().toLowerCase();
    if (q) {
      posts = posts.filter((p) => {
        const combined =
          (p.title || "") +
          " " +
          (p.text || "") +
          " " +
          (p.authorName || "");
        return combined.toLowerCase().includes(q);
      });
    }

    if (state.sortMode === "newest") {
      posts.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
    } else if (state.sortMode === "oldest") {
      posts.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
    } else if (state.sortMode === "activity") {
      posts.sort((a, b) => getLastActivityTime(b) - getLastActivityTime(a));
    }

    // Admin view filters (only apply for moderators/admins)
    if (isModerator()) {
      if (state.adminView === "pending") {
        posts = posts.filter((p) => p.status === "pending");
      } else if (state.adminView === "mine") {
        const currentEmail = window.RCCS_CUSTOMER
          ? (window.RCCS_CUSTOMER.email || "").toLowerCase()
          : "";
        posts = posts.filter(
          (p) =>
            (p.authorEmail || "").toLowerCase() === currentEmail
        );
      }
    }

    // hide pending posts from non-mods
    if (!isModerator()) {
      posts = posts.filter((p) => p.status !== "pending");
    }

    return posts;
  }

  /* --------------------------------------------------------
     URL handling (links + basic embeds)
     -------------------------------------------------------- */

  function renderTextWithLinks(text) {
    if (!text) return "";
    let result = "";
    let lastIndex = 0;

    text.replace(URL_REGEX, (match, offset) => {
      if (offset > lastIndex) {
        result += escapeHtml(text.slice(lastIndex, offset));
      }
      const url = match;
      result += `<a href="${escapeHtml(url)}" target="_blank" rel="noopener">${escapeHtml(url)}</a>`;
      lastIndex = offset + match.length;
      return match;
    });

    if (lastIndex < text.length) {
      result += escapeHtml(text.slice(lastIndex));
    }
    return result;
  }

  function buildYoutubeEmbed(url) {
    try {
      const u = new URL(url);
      let videoId = "";
      if (u.hostname.includes("youtu.be")) {
        videoId = u.pathname.replace("/", "");
      } else if (u.hostname.includes("youtube.com")) {
        videoId = u.searchParams.get("v") || "";
      }
      if (!videoId) return "";
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return `
        <div class="rccs-embed-youtube">
          <iframe src="${embedUrl}" allowfullscreen loading="lazy"></iframe>
        </div>
      `;
    } catch (e) {
      return "";
    }
  }

  function buildLinkCard(url) {
    let host = "";
    try {
      const u = new URL(url);
      host = u.host;
    } catch (e) {
      host = "";
    }

    const isProduct =
      host &&
      window.location &&
      host === window.location.host &&
      url.indexOf("/products/") !== -1;

    const title = isProduct ? "Store link" : "Link preview";

    return `
      <div class="rccs-embed-card">
        <div class="rccs-embed-card-title">${escapeHtml(title)}</div>
        <div class="rccs-embed-card-url">
          <a href="${escapeHtml(url)}" target="_blank" rel="noopener">
            ${escapeHtml(url)}
          </a>
        </div>
      </div>
    `;
  }

  function buildEmbeds(text) {
    if (!text) return "";
    const seen = new Set();
    const embeds = [];

    let match;
    while ((match = URL_REGEX.exec(text)) !== null) {
      const url = match[0];
      if (seen.has(url)) continue;
      seen.add(url);

      if (
        url.includes("youtube.com/watch") ||
        url.includes("youtu.be/")
      ) {
        const yt = buildYoutubeEmbed(url);
        if (yt) {
          embeds.push(yt);
          continue;
        }
      }

      embeds.push(buildLinkCard(url));
    }

    if (!embeds.length) return "";
    return `<div class="rccs-embed-container">${embeds.join("")}</div>`;
  }

  /* --------------------------------------------------------
     FETCH POSTS (demo)
     -------------------------------------------------------- */

  async function fetchPosts() {
    state.loading = true;
    renderPosts();

    if (USE_DEMO) {
      state.posts = demoPostsBase.map((p) => {
        const reactions = (p.reactions || []).map((r) => ({ ...r }));
        const userReactions = {};
        const likedBy = [];
        reactions.forEach((r) => {
          userReactions[r.userId] = r.reaction;
          likedBy.push(r.userId);
        });
        return {
          ...p,
          status: p.status || "approved",
          comments: p.comments ? p.comments.map((c) => ({ ...c })) : [],
          reactions,
          userReactions,
          likedBy,
          likes: likedBy.length
        };
      });
      state.loading = false;
      renderModerationSettingsUI();
      renderPosts();
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/posts`);
      const data = await res.json();
      state.posts = (data.posts || []).map((p) => ({
        ...p,
        status: p.status || "approved"
      }));
    } catch (err) {
      console.error("POST LOAD ERROR:", err);
    }
    state.loading = false;
    renderModerationSettingsUI();
    renderPosts();
  }

  /* --------------------------------------------------------
     MODERATION ACTIONS (demo + API stub)
     -------------------------------------------------------- */

  function approvePost(postId) {
    if (!isModerator()) return;

    if (USE_DEMO) {
      const p = state.posts.find((x) => x.id === postId);
      if (p) p.status = "approved";
      renderPosts();
      return;
    }

    fetch(`${API_BASE}/posts/${postId}/approve`, {
      method: "POST"
    })
      .then(() => fetchPosts())
      .catch((err) => console.error("APPROVE ERROR:", err));
  }

  function rejectPost(postId) {
    if (!isModerator()) return;

    if (USE_DEMO) {
      state.posts = state.posts.filter((x) => x.id !== postId);
      renderPosts();
      return;
    }

    fetch(`${API_BASE}/posts/${postId}/reject`, {
      method: "POST"
    })
      .then(() => fetchPosts())
      .catch((err) => console.error("REJECT ERROR:", err));
  }

  function deletePost(postId) {
    if (!isModerator()) return;

    if (USE_DEMO) {
      state.posts = state.posts.filter((x) => x.id !== postId);
      renderPosts();
      return;
    }

    fetch(`${API_BASE}/posts/${postId}`, {
      method: "DELETE"
    })
      .then(() => fetchPosts())
      .catch((err) => console.error("DELETE ERROR:", err));
  }

  /* --------------------------------------------------------
     RENDER POSTS + PAGINATION
     -------------------------------------------------------- */

  function renderPagination(totalCount) {
    const container = document.getElementById("rccs-pagination");
    if (!container) return;

    const pageSize = state.pageSize;
    const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
    state.currentPage = Math.min(
      Math.max(1, state.currentPage),
      pageCount
    );

    if (pageCount <= 1) {
      container.innerHTML = "";
      return;
    }

    let buttons = "";

    buttons += `<button class="rccs-page-button" data-page="prev" ${
      state.currentPage === 1 ? "disabled" : ""
    }>Prev</button>`;

    for (let i = 1; i <= pageCount; i++) {
      const active = i === state.currentPage ? "is-active" : "";
      buttons += `<button class="rccs-page-button ${active}" data-page="${i}">${i}</button>`;
    }

    buttons += `<button class="rccs-page-button" data-page="next" ${
      state.currentPage === pageCount ? "disabled" : ""
    }>Next</button>`;

    container.innerHTML = buttons;

    container
      .querySelectorAll(".rccs-page-button")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          const p = btn.getAttribute("data-page");
          if (p === "prev") {
            if (state.currentPage > 1) state.currentPage--;
          } else if (p === "next") {
            const pc = Math.max(
              1,
              Math.ceil(totalCount / state.pageSize)
            );
            if (state.currentPage < pc) state.currentPage++;
          } else {
            state.currentPage = parseInt(p, 10) || 1;
          }
          renderPosts();
        });
      });
  }

  function renderPosts() {
    const list = document.getElementById("rccs-post-list");
    if (!list) return;

    const currentUserId = getCurrentUserId();

    if (state.loading) {
      list.textContent = "Loading posts…";
      return;
    }

    const allPosts = getFilteredSortedPosts();
    const total = allPosts.length;

    renderPagination(total);

    if (!total) {
      list.innerHTML = `<p style="text-align:center;color:#9ca3af;">No posts yet — be the first to share!</p>`;
      return;
    }

    const pageSize = state.pageSize;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    state.currentPage = Math.min(
      Math.max(1, state.currentPage),
      pageCount
    );
    const startIndex = (state.currentPage - 1) * pageSize;
    const pagePosts = allPosts.slice(startIndex, startIndex + pageSize);

    list.innerHTML = "";

    pagePosts.forEach((post) => {
      const el = document.createElement("div");
      el.className = "rccs-forum-post";
      el.dataset.postId = post.id;
      el.dataset.status = post.status || "approved";
      el.id = `rccs-post-${post.id}`;
      if (post.status === "pending") {
        el.classList.add("rccs-forum-post--pending");
      }

      const reactionsArr = post.reactions || [];
      const userReactions = post.userReactions || {};
      const userReactionId = currentUserId
        ? userReactions[currentUserId]
        : null;

      const reactionDef =
        REACTIONS.find((r) => r.id === userReactionId) || REACTIONS[0];

      // build counts for summary
      const counts = {};
      reactionsArr.forEach((r) => {
        counts[r.reaction] = (counts[r.reaction] || 0) + 1;
      });
      const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      const top = sorted.slice(0, 3);

      const summaryHtml = reactionsArr.length
        ? `
      <div class="rccs-reaction-summary-container">
        <div class="rccs-reaction-summary" data-summary="${post.id}">
          <span class="rccs-summary-emoji-wrap">
            ${top
              .map(([id]) => {
                const def = REACTIONS.find((r) => r.id === id);
                return `<span class="rccs-summary-emoji">${def ? def.emoji : ""}</span>`;
              })
              .join("")}
          </span>
          <span class="rccs-summary-count">${reactionsArr.length}</span>
        </div>
        <div class="rccs-reaction-list" data-reaction-list="${post.id}">
          ${reactionsArr
            .map((r) => {
              const def = REACTIONS.find((x) => x.id === r.reaction);
              const emoji = def ? def.emoji : "👍";
              return `
              <div class="rccs-reaction-list-item">
                <span class="rccs-reaction-list-item-emoji">${emoji}</span>
                <span>${escapeHtml(r.userName || "User")}</span>
              </div>`;
            })
            .join("")}
        </div>
      </div>`
        : "";

      const totalReactions = reactionsArr.length;

      // media
      let mediaHtml = "";
      if (post.videoUrl) {
        mediaHtml = `
          <video
            class="rccs-forum-post-media rccs-forum-post-video"
            src="${post.videoUrl}"
            controls
            playsinline
          ></video>`;
      } else if (post.imageUrl) {
        mediaHtml = `
          <img
            class="rccs-forum-post-media rccs-forum-post-image"
            src="${post.imageUrl}"
            alt="RC media"
          >`;
      }

      const title = post.title ? escapeHtml(post.title) : "";
      const textHtml = renderTextWithLinks(post.text || "");
      const embedHtml = buildEmbeds(post.text || "");

      const allComments = post.comments || [];
      const hasMoreComments = allComments.length > 3;
      const visibleComments = hasMoreComments
        ? allComments.slice(-3)
        : allComments;

      const currentUserIsMod = isModerator();
      const isPending = post.status === "pending";

      const modBadgeHtml = isPending
        ? `<span class="rccs-post-status-badge rccs-post-status-pending">Pending approval</span>`
        : "";

      const modPanelHtml = currentUserIsMod
        ? `
        <div class="rccs-mod-post-panel">
          ${isPending ? `<span class="rccs-mod-post-note">Hidden from guests until approved.</span>` : ""}
          <div class="rccs-mod-post-buttons">
            ${
              isPending
                ? `<button type="button" class="rccs-mod-btn rccs-mod-btn-approve" data-mod-approve="${post.id}">Approve</button>`
                : ""
            }
            <button type="button" class="rccs-mod-btn rccs-mod-btn-reject" data-mod-reject="${post.id}">Reject/Delete</button>
          </div>
        </div>`
        : "";

      el.innerHTML = `
        <div class="rccs-forum-post-header">
          <div class="rccs-post-header-top">
            <span class="rccs-post-author">${escapeHtml(
              post.authorName
            )}</span>
            <div class="rccs-post-header-right">
              ${modBadgeHtml}
              <span class="rccs-post-time">${formatRelativeTime(
                post.createdAt
              )}</span>
            </div>
          </div>
          ${
            title
              ? `<div class="rccs-post-title-line">${title}</div>`
              : ""
          }
        </div>

        <div class="rccs-post-separator"></div>

        <div class="rccs-forum-post-body">
          <p>${textHtml}</p>
          ${embedHtml}
          ${mediaHtml}
        </div>

        <div class="rccs-forum-actions">
          <div class="rccs-reaction-wrapper" data-post-id="${post.id}">
            <button
              class="rccs-like-button ${
                userReactionId
                  ? "is-liked reaction-" + userReactionId
                  : ""
              }"
              data-role="main-reaction"
              data-id="${post.id}"
              type="button"
            >
              <span class="rccs-like-emoji">${reactionDef.emoji}</span>
              <span class="rccs-like-label">${reactionDef.label.toUpperCase()}</span>
              <span class="rccs-like-count">(${totalReactions})</span>
            </button>

            <div class="rccs-reaction-bar" data-reaction-bar="${post.id}">
              ${REACTIONS.map(
                (r) => `
                <button
                  class="rccs-reaction-option"
                  data-reaction="${r.id}"
                  aria-label="${r.label}"
                  type="button"
                >
                  ${r.emoji}
                </button>`
              ).join("")}
            </div>
          </div>

          <button
            class="rccs-comment-button"
            data-action="comment-focus"
            data-id="${post.id}"
            type="button"
          >
            💬 Comment
          </button>

          <button
            class="rccs-share-button"
            data-action="share"
            data-id="${post.id}"
            type="button"
          >
            ⤴ Share
          </button>

          <span>${allComments.length} comments</span>

          ${summaryHtml}
        </div>

        ${modPanelHtml}

        <div class="rccs-forum-comment-list">
          ${
            hasMoreComments
              ? `<button class="rccs-view-all-comments" data-id="${post.id}">
                   View all ${allComments.length} comments
                 </button>`
              : ""
          }

          ${visibleComments
            .map((c) => {
              const isReply = /^\s*@/.test(c.text || "");
              return `
              <div class="rccs-forum-comment ${
                isReply ? "rccs-forum-comment-reply" : ""
              }">
                <span class="rccs-forum-comment-author">${escapeHtml(
                  c.authorName
                )}:</span>
                ${escapeHtml(c.text)}
                <button
                  class="rccs-comment-reply"
                  data-role="comment-reply"
                  data-post-id="${post.id}"
                  data-author="${escapeHtml(c.authorName)}"
                  type="button"
                >
                  Reply
                </button>
              </div>
            `;
            })
            .join("")}

          <div class="rccs-forum-new-comment">
            <input type="text" placeholder="${
              isLoggedIn ? "Comment…" : "Sign in to comment…"
            }" data-comment-input="${post.id}" ${
        isLoggedIn ? "" : "disabled"
      }>
            <button data-action="comment" data-id="${post.id}">
              ${isLoggedIn ? "Send" : "Sign in"}
            </button>
          </div>
        </div>
      `;

      list.appendChild(el);
    });

    wireUpReactions(list);
    wireUpReactionLists(list);
    wireUpComments(list);
    wireUpShareButtons(list);
    wireUpCommentModals(list);
    wireUpModButtons(list);
  }

  function wireUpModButtons(list) {
    if (!isModerator()) return;

    list.querySelectorAll("[data-mod-approve]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const postId = btn.getAttribute("data-mod-approve");
        approvePost(postId);
      });
    });

    list.querySelectorAll("[data-mod-reject]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const postId = btn.getAttribute("data-mod-reject");
        if (confirm("Remove this post from the forum?")) {
          rejectPost(postId);
        }
      });
    });
  }

  /* --------------------------------------------------------
     COMMENTS + REPLIES + VIEW ALL MODAL
     -------------------------------------------------------- */

  function wireUpComments(list) {
    list.querySelectorAll("button[data-action='comment']").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (!isLoggedIn) {
          window.location.href = loginUrl;
          return;
        }
        const id = btn.getAttribute("data-id");
        const input = list.querySelector(`input[data-comment-input="${id}"]`);
        if (input && input.value.trim()) {
          addComment(id, input.value.trim());
          input.value = "";
        }
      });
    });

    list
      .querySelectorAll("button[data-action='comment-focus']")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          if (!isLoggedIn) {
            window.location.href = loginUrl;
            return;
          }
          const id = btn.getAttribute("data-id");
          const input = list.querySelector(`input[data-comment-input="${id}"]`);
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
      });

    list
      .querySelectorAll("button[data-role='comment-reply']")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          if (!isLoggedIn) {
            window.location.href = loginUrl;
            return;
          }
          const postId = btn.getAttribute("data-post-id");
          const author = btn.getAttribute("data-author") || "";
          const input = list.querySelector(
            `input[data-comment-input="${postId}"]`
          );
          if (input) {
            const mention = `@${author} `;
            if (!input.value.startsWith(mention)) {
              input.value = mention + input.value;
            }
            input.focus();
          }
        });
      });
  }

  function wireUpCommentModals(list) {
    list
      .querySelectorAll(".rccs-view-all-comments")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          const postId = btn.getAttribute("data-id");
          openCommentModal(postId);
        });
      });
  }

  function openCommentModal(postId) {
    const post = state.posts.find((p) => p.id === postId);
    if (!post) return;

    const backdrop = document.createElement("div");
    backdrop.className = "rccs-comment-modal-backdrop";

    const modal = document.createElement("div");
    modal.className = "rccs-comment-modal";

    const title = post.title || "Comments";
    const textHtml = renderTextWithLinks(post.text || "");
    const embedHtml = buildEmbeds(post.text || "");
    const mediaHtml = post.imageUrl
      ? `<img class="rccs-forum-post-media rccs-forum-post-image" src="${post.imageUrl}" alt="RC media">`
      : post.videoUrl
      ? `<video class="rccs-forum-post-media rccs-forum-post-video" src="${post.videoUrl}" controls playsinline></video>`
      : "";

    modal.innerHTML = `
      <div class="rccs-comment-modal-header">
        <div class="rccs-comment-modal-title">${escapeHtml(
          post.authorName
        )} • ${escapeHtml(title)}</div>
        <button class="rccs-comment-modal-close">Back</button>
      </div>
      <div class="rccs-comment-modal-body">
        <p>${textHtml}</p>
        ${embedHtml}
        ${mediaHtml}
        <div class="rccs-forum-comment-list" style="border-top:none;margin-top:8px;">
          ${(post.comments || [])
            .map((c) => {
              const isReply = /^\s*@/.test(c.text || "");
              return `
              <div class="rccs-forum-comment ${
                isReply ? "rccs-forum-comment-reply" : ""
              }">
                <span class="rccs-forum-comment-author">${escapeHtml(
                  c.authorName
                )}:</span>
                ${escapeHtml(c.text)}
              </div>`;
            })
            .join("")}
        </div>
      </div>
    `;

    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const close = () => {
      document.body.style.overflow = prevOverflow;
      backdrop.remove();
    };

    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) close();
    });

    modal
      .querySelector(".rccs-comment-modal-close")
      .addEventListener("click", close);
  }

  /* --------------------------------------------------------
     REACTIONS + LIKE BUTTON
     -------------------------------------------------------- */

  function wireUpReactions(list) {
    const wrappers = list.querySelectorAll(".rccs-reaction-wrapper");
    const currentUserId = getCurrentUserId();
    const userIsLoggedIn = !!currentUserId;

    wrappers.forEach((wrapper) => {
      const postId = wrapper.getAttribute("data-post-id");
      const mainBtn = wrapper.querySelector("button[data-role='main-reaction']");
      const bar = wrapper.querySelector(".rccs-reaction-bar");
      if (!mainBtn || !bar) return;

      // If not logged in: clicking LIKE sends to login, no reaction bar.
      if (!userIsLoggedIn) {
        mainBtn.addEventListener("click", () => {
          window.location.href = loginUrl;
        });
        return;
      }

      let showTimer = null;
      let hideTimer = null;
      let touchTimer = null;
      let longPress = false;
      let activeOption = null;

      const getPostData = () => state.posts.find((p) => p.id === postId);

      /* -------- Desktop (mouse) -------- */
      if (!isTouchDevice) {
        mainBtn.addEventListener("click", () => {
          const post = getPostData();
          const current = (post.userReactions || {})[currentUserId];

          if (current) {
            setReaction(postId, current, currentUserId);
          } else {
            setReaction(postId, "like", currentUserId);
          }
        });

        mainBtn.addEventListener("mouseenter", () => {
          if (hideTimer) {
            clearTimeout(hideTimer);
            hideTimer = null;
          }
          showTimer = setTimeout(() => {
            bar.classList.add("is-visible");
            requestAnimationFrame(() => adjustReactionBarPosition(bar));
          }, 180);
        });

        mainBtn.addEventListener("mouseleave", () => {
          if (showTimer) {
            clearTimeout(showTimer);
            showTimer = null;
          }
          hideTimer = setTimeout(() => {
            bar.classList.remove("is-visible");
          }, 320);
        });

        bar.addEventListener("mouseenter", () => {
          if (hideTimer) {
            clearTimeout(hideTimer);
            hideTimer = null;
          }
          bar.classList.add("is-visible");
          requestAnimationFrame(() => adjustReactionBarPosition(bar));
        });

        bar.addEventListener("mouseleave", () => {
          hideTimer = setTimeout(() => {
            bar.classList.remove("is-visible");
          }, 200);
        });

        bar.querySelectorAll(".rccs-reaction-option").forEach((opt) => {
          opt.addEventListener("click", () => {
            const reactionId = opt.getAttribute("data-reaction");
            setReaction(postId, reactionId, currentUserId);
            bar.classList.remove("is-visible");
          });
        });

      } else {
        /* -------- Mobile / touch -------- */

        mainBtn.addEventListener(
          "touchstart",
          (e) => {
            longPress = false;
            activeOption = null;
            if (touchTimer) clearTimeout(touchTimer);
            if (hideTimer) clearTimeout(hideTimer);

            touchTimer = setTimeout(() => {
              longPress = true;
              bar.classList.add("is-visible");
              requestAnimationFrame(() => adjustReactionBarPosition(bar));
            }, 400);

            e.preventDefault();
          },
          { passive: false }
        );

        mainBtn.addEventListener(
          "touchend",
          (e) => {
            if (touchTimer) {
              clearTimeout(touchTimer);
              touchTimer = null;
            }

            if (!longPress) {
              const post = getPostData();
              const current = (post.userReactions || {})[currentUserId];

              if (current) {
                setReaction(postId, current, currentUserId);
              } else {
                setReaction(postId, "like", currentUserId);
              }
            } else {
              if (activeOption) {
                const reactionId = activeOption.getAttribute("data-reaction");
                setReaction(postId, reactionId, currentUserId);
              }
              bar.classList.remove("is-visible");
            }

            longPress = false;
            activeOption = null;
            e.preventDefault();
          },
          { passive: false }
        );

        function handleTouchMoveOverBar(e) {
          if (!longPress) return;
          e.preventDefault();
          if (!bar.classList.contains("is-visible")) return;

          const touch = e.touches[0];
          const x = touch.clientX;
          const y = touch.clientY;

          const options = bar.querySelectorAll(".rccs-reaction-option");
          let found = null;
          options.forEach((opt) => {
            const rect = opt.getBoundingClientRect();
            if (
              x >= rect.left &&
              x <= rect.right &&
              y >= rect.top &&
              y <= rect.bottom
            ) {
              found = opt;
            }
          });

          if (activeOption && activeOption !== found) {
            activeOption.classList.remove("is-hot");
          }
          if (found && found !== activeOption) {
            found.classList.add("is-hot");
            activeOption = found;
          }
        }

        mainBtn.addEventListener("touchmove", handleTouchMoveOverBar, {
          passive: false
        });
        bar.addEventListener("touchmove", handleTouchMoveOverBar, {
          passive: false
        });

        bar.addEventListener(
          "touchend",
          (e) => {
            if (longPress && activeOption) {
              const reactionId = activeOption.getAttribute("data-reaction");
              setReaction(postId, reactionId, currentUserId);
            }
            bar.classList.remove("is-visible");
            longPress = false;
            activeOption = null;
            e.preventDefault();
          },
          { passive: false }
        );
      }
    });
  }

  /* --------------------------------------------------------
     WHO-REACTED LIST (summary tooltip)
     -------------------------------------------------------- */

  function wireUpReactionLists(list) {
    const summaries = list.querySelectorAll(".rccs-reaction-summary");

    summaries.forEach((summary) => {
      const postId = summary.getAttribute("data-summary");
      const tooltip = list.querySelector(
        `.rccs-reaction-list[data-reaction-list="${postId}"]`
      );
      if (!tooltip) return;

      let hideTimer = null;

      function show() {
        if (hideTimer) clearTimeout(hideTimer);
        tooltip.classList.add("is-visible");
      }

      function hide() {
        hideTimer = setTimeout(() => {
          tooltip.classList.remove("is-visible");
        }, 220);
      }

      summary.addEventListener("mouseenter", show);
      summary.addEventListener("mouseleave", hide);
      tooltip.addEventListener("mouseenter", show);
      tooltip.addEventListener("mouseleave", hide);

      // mobile tap toggles
      summary.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const visible = tooltip.classList.contains("is-visible");
        document
          .querySelectorAll(".rccs-reaction-list.is-visible")
          .forEach((el) => el.classList.remove("is-visible"));
        if (!visible) tooltip.classList.add("is-visible");
      });
    });

    document.addEventListener("click", () => {
      document
        .querySelectorAll(".rccs-reaction-list.is-visible")
        .forEach((el) => el.classList.remove("is-visible"));
    });
  }

  /* --------------------------------------------------------
     SHARE BUTTON
     -------------------------------------------------------- */

  function wireUpShareButtons(list) {
    list.querySelectorAll("button[data-action='share']").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const postId = btn.getAttribute("data-id");
        const url = `${window.location.origin}${window.location.pathname}#rccs-post-${postId}`;

        if (navigator.share) {
          try {
            await navigator.share({
              title: "RCCS Community Garage",
              text: "Check out this RC rig on the RCCS forum!",
              url
            });
          } catch (e) {
            console.warn("Share canceled", e);
          }
        } else if (navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(url);
            alert("Link copied. Paste it anywhere to share this post!");
          } catch (e) {
            alert("Unable to copy link, but you can share this page URL.");
          }
        } else {
          alert("Share not supported on this device, but you can copy the URL.");
        }
      });
    });
  }

  /* --------------------------------------------------------
     SET REACTION (demo + API stub)
     -------------------------------------------------------- */

  function setReaction(postId, reactionId, userId) {
    if (!userId) userId = getCurrentUserId();
    if (!userId) {
      window.location.href = loginUrl;
      return;
    }

    if (USE_DEMO) {
      const post = state.posts.find((p) => p.id === postId);
      if (!post) return;

      post.reactions = post.reactions || [];
      const idx = post.reactions.findIndex((r) => r.userId === userId);

      if (idx !== -1 && post.reactions[idx].reaction === reactionId) {
        // same reaction again -> remove (unlike)
        post.reactions.splice(idx, 1);
      } else if (idx !== -1) {
        // change reaction type
        post.reactions[idx].reaction = reactionId;
      } else {
        // new reaction
        post.reactions.push({
          userId,
          userName: getCurrentUserName(),
          reaction: reactionId
        });
      }

      post.userReactions = {};
      post.likedBy = [];
      post.reactions.forEach((r) => {
        post.userReactions[r.userId] = r.reaction;
        post.likedBy.push(r.userId);
      });
      post.likes = post.reactions.length;

      renderPosts();
      return;
    }

    fetch(`${API_BASE}/posts/${postId}/react`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reaction: reactionId, userId })
    })
      .then(() => fetchPosts())
      .catch((err) => console.error("REACTION ERROR:", err));
  }

  /* --------------------------------------------------------
     CREATE POST (with moderation rules)
     -------------------------------------------------------- */

  async function createPost() {
    if (!isLoggedIn) {
      window.location.href = loginUrl;
      return;
    }

    const titleEl = document.getElementById("rccs-new-post-title");
    const textEl = document.getElementById("rccs-new-post-text");
    const mediaEl = document.getElementById("rccs-new-post-media");
    const statusEl = document.getElementById("rccs-new-post-status");

    const title = titleEl.value.trim();
    const text = textEl.value.trim();
    if (!text) {
      statusEl.textContent = "Write something first.";
      return;
    }

    const file = mediaEl.files[0];
    const hasMedia = !!file;
    const hasLink = HAS_URL_REGEX.test(text);

    // Determine if this post should go to "pending" based on moderation settings
    let status = "approved";
    let willBePending = false;

    if (state.moderation.approvalEnabled) {
      if (state.moderation.mediaLinksOnly) {
        if (hasMedia || hasLink) {
          status = "pending";
          willBePending = true;
        }
      } else {
        status = "pending";
        willBePending = true;
      }
    }

    if (USE_DEMO) {
      statusEl.textContent = willBePending
        ? "Post submitted for review (demo). Pending posts are only visible to mods."
        : "Adding demo post…";

      const addDemoPost = (imageUrl, videoUrl) => {
        const newPost = {
          id: "demo_" + Date.now(),
          title,
          authorName: getCurrentUserName(),
          authorEmail: window.RCCS_CUSTOMER
            ? window.RCCS_CUSTOMER.email || ""
            : "",
          text,
          imageUrl: imageUrl || "",
          videoUrl: videoUrl || "",
          createdAt: new Date().toISOString(),
          status,
          comments: [],
          reactions: [],
          userReactions: {},
          likedBy: [],
          likes: 0
        };
        state.posts.unshift(newPost);
        titleEl.value = "";
        textEl.value = "";
        mediaEl.value = "";
        const fnEl = document.getElementById("rccs-upload-filename");
        if (fnEl) fnEl.textContent = "";
        if (!willBePending) {
          statusEl.textContent = "Posted (demo only — refresh will reset).";
        } else {
          statusEl.textContent =
            "Submitted (demo). Only moderators see pending posts.";
        }
        renderPosts();
      };

      if (file) {
        const reader = new FileReader();
        const isVideo = file.type && file.type.startsWith("video");

        reader.onload = function (e) {
          const url = e.target.result;
          if (isVideo) {
            addDemoPost("", url);
          } else {
            addDemoPost(url, "");
          }
        };
        reader.onerror = function () {
          console.error("Media preview failed");
          addDemoPost("", "");
        };
        reader.readAsDataURL(file);
      } else {
        addDemoPost("", "");
      }

      return;
    }

    // REAL API MODE
    statusEl.textContent = "Posting…";
    const formData = new FormData();
    formData.append("title", title);
    formData.append("text", text);
    formData.append("authorId", window.RCCS_CUSTOMER.id);
    formData.append("authorName", getCurrentUserName());
    formData.append("authorEmail", window.RCCS_CUSTOMER.email);
    formData.append("status", status);
    if (mediaEl.files[0]) formData.append("media", mediaEl.files[0]);

    try {
      await fetch(`${API_BASE}/posts`, {
        method: "POST",
        body: formData
      });
      titleEl.value = "";
      textEl.value = "";
      mediaEl.value = "";
      statusEl.textContent = willBePending
        ? "Submitted for review."
        : "Posted!";
      await fetchPosts();
    } catch (err) {
      statusEl.textContent = "ERROR — unable to post.";
      console.error("POST ERROR:", err);
    }
  }

  /* --------------------------------------------------------
     ADD COMMENT
     -------------------------------------------------------- */

  async function addComment(id, text) {
    if (!isLoggedIn) {
      window.location.href = loginUrl;
      return;
    }

    if (USE_DEMO) {
      const post = state.posts.find((p) => p.id === id);
      if (!post) return;
      post.comments = post.comments || [];
      post.comments.push({
        authorName: getCurrentUserName(),
        text,
        createdAt: new Date().toISOString()
      });
      renderPosts();
      return;
    }

    try {
      await fetch(`${API_BASE}/posts/${id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text,
          authorId: window.RCCS_CUSTOMER.id,
          authorName: getCurrentUserName()
        })
      });
      await fetchPosts();
    } catch (err) {
      console.error("COMMENT ERROR:", err);
    }
  }

  /* --------------------------------------------------------
     INBOX / DM DRAWER (DEMO)
     -------------------------------------------------------- */

  const demoInboxThreads = [
    {
      id: "t1",
      name: "Alex",
      time: "2h ago",
      messages: [
        { fromMe: false, text: "Hey! Got a question about your panel set." },
        { fromMe: false, text: "Thinking about running it on a comp rig." },
        { fromMe: true,  text: "Sure thing – happy to help you dial it in." }
      ]
    },
    {
      id: "t2",
      name: "Jordan",
      time: "1d ago",
      messages: [
        { fromMe: false, text: "Those gate markers in your photos look sick." },
        { fromMe: true,  text: "Thanks! They’re the RCCS ones from the store." }
      ]
    }
  ];

  function renderInbox() {
    const body = document.getElementById("rccs-inbox-body");
    if (!body) return;

    if (!isLoggedIn) {
      body.innerHTML =
        '<p class="rccs-inbox-empty">Sign in to send and receive private messages.</p>';
      return;
    }

    const threadsHtml = demoInboxThreads
      .map((t, idx) => {
        const msgs = t.messages
          .map(
            (m) => `
              <div class="rccs-inbox-message ${
                m.fromMe
                  ? "rccs-inbox-message--me"
                  : "rccs-inbox-message--other"
              }">
                ${escapeHtml(m.text)}
              </div>
            `
          )
          .join("");

        return `
          <div class="rccs-inbox-thread" data-thread-index="${idx}">
            <div class="rccs-inbox-thread-header">
              <span class="rccs-inbox-thread-name">${escapeHtml(
                t.name
              )}</span>
              <span class="rccs-inbox-thread-time">${escapeHtml(
                t.time
              )}</span>
            </div>
            <div class="rccs-inbox-thread-messages">
              ${msgs}
            </div>
            <div class="rccs-inbox-reply-row">
              <input
                type="text"
                placeholder="Reply…"
                data-inbox-input="${idx}"
              >
              <button type="button" data-inbox-send="${idx}">
                Send
              </button>
            </div>
          </div>
        `;
      })
      .join("");

    body.innerHTML =
      threadsHtml +
      '<p class="rccs-inbox-note">Demo inbox only — messages reset on refresh.</p>';

    wireInboxReplyHandlers();
  }

  function wireInboxReplyHandlers() {
    const body = document.getElementById("rccs-inbox-body");
    if (!body || !isLoggedIn) return;

    body.querySelectorAll("button[data-inbox-send]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = parseInt(btn.getAttribute("data-inbox-send"), 10);
        const input = body.querySelector(
          `input[data-inbox-input="${idx}"]`
        );
        if (!input || !input.value.trim()) return;

        demoInboxThreads[idx].messages.push({
          fromMe: true,
          text: input.value.trim()
        });
        input.value = "";
        renderInbox();
      });
    });
  }

  function setupInbox() {
    const inbox = document.getElementById("rccs-inbox");
    if (!inbox) return;

    const openBtn = document.querySelector("[data-open-inbox]");
    const closeBtn = document.querySelector("[data-close-inbox]");

    if (openBtn) {
      openBtn.addEventListener("click", () => {
        if (!isLoggedIn) {
          // guests – send them to login
          window.location.href = loginUrl;
          return;
        }
        renderInbox();
        inbox.classList.add("is-open");
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        inbox.classList.remove("is-open");
      });
    }

    inbox.addEventListener("click", (e) => {
      if (e.target === inbox) {
        inbox.classList.remove("is-open");
      }
    });

    renderInbox();
  }

  /* --------------------------------------------------------
     INITIAL WIRING
     -------------------------------------------------------- */

  const createBtn = document.getElementById("rccs-create-post");
  if (createBtn) {
    createBtn.addEventListener("click", createPost);
  }

  const mediaInput = document.getElementById("rccs-new-post-media");
  const filenameEl = document.getElementById("rccs-upload-filename");
  if (mediaInput && filenameEl) {
    mediaInput.addEventListener("change", () => {
      if (mediaInput.files && mediaInput.files[0]) {
        filenameEl.textContent = mediaInput.files[0].name;
      } else {
        filenameEl.textContent = "";
      }
    });
  }

  setupInbox();
  renderModerationSettingsUI();
  fetchPosts();
})();
